/**
 * Extracts the snapshot fields that the pin-item endpoint expects (preview +
 * role + optional parent anchor) from a rendered ChatItem.
 *
 * The backend accepts arbitrary opaque item_ids (real message UUIDs and
 * composite render-only IDs like `<parent-uuid>-tc-0`). This helper tells the
 * backend what the pin represents visually, so the popover can show a useful
 * row even when the item is not a first-class message.
 */

import type {
  ChatAgentReport,
  ChatContentBrief,
  ChatItem,
  ChatMessage,
  ChatNote,
  ChatToolCall,
  ChatUserInteraction,
} from '@/components/chat/chat-message.interface'

const UUID_LENGTH = 36
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export const PIN_PREVIEW_MAX_CHARS = 300

export interface PinSnapshot {
  parentMessageId: string | null
  itemPreview: string
  itemRole: string
}

const extractParentMessageId = (itemId: string): string | null => {
  if (UUID_PATTERN.test(itemId)) return itemId
  const prefix = itemId.slice(0, UUID_LENGTH)
  if (UUID_PATTERN.test(prefix)) return prefix
  return null
}

const toolCallPreview = (item: ChatToolCall): string =>
  item.friendlyName ?? item.toolName ?? 'Tool call'

const messagePreview = (item: ChatMessage | ChatNote): string => item.content ?? ''

const agentReportPreview = (item: ChatAgentReport): string =>
  item.reportContent || item.agentName || 'Agent report'

const contentBriefPreview = (item: ChatContentBrief): string =>
  item.excerpt || item.name || 'Content brief'

const userInteractionPreview = (item: ChatUserInteraction): string => item.title || 'User action'

const pickPreview = (item: ChatItem): string => {
  if (item.type === 'message' || item.type === 'note') return messagePreview(item)
  if (item.type === 'tool-call') return toolCallPreview(item)
  if (item.type === 'agent-report') return agentReportPreview(item)
  if (item.type === 'content-brief') return contentBriefPreview(item)
  if (item.type === 'user-interaction') return userInteractionPreview(item)
  return item.type
}

const ITEM_TYPE_TO_ROLE: Record<ChatItem['type'], string> = {
  message: 'MESSAGE',
  note: 'NOTE',
  'tool-call': 'TOOL_CALL',
  thinking: 'THINKING',
  'content-brief': 'CONTENT_BRIEF',
  'user-interaction': 'USER_INTERACTION',
  'agent-report': 'AGENT_REPORT',
  delegation: 'DELEGATION',
}

export const buildPinSnapshot = (item: ChatItem): PinSnapshot => ({
  parentMessageId: extractParentMessageId(item.id),
  itemPreview: pickPreview(item).slice(0, PIN_PREVIEW_MAX_CHARS),
  itemRole: ITEM_TYPE_TO_ROLE[item.type],
})
